import React, { useState, useEffect, useRef } from 'react';
import './NodeDropbox.css';

const NodeDropbox = ({ 
  onSearch, 
  onSelectEnvironment, 
  onSelectCluster, 
  environments, 
  selectedClusters = [],
  environmentData = []
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [clusterSearchTerm, setClusterSearchTerm] = useState('');
  const [selectedClusterSet, setSelectedClusterSet] = useState(new Set());
  const [isClusterOpen, setIsClusterOpen] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [showClusterTooltip, setShowClusterTooltip] = useState(false);
  const [allClustersSelected, setAllClustersSelected] = useState(false);

  const clusterDropdownRef = useRef(null);

  // Initialize selectedClusterSet from props when it changes
  useEffect(() => {
    if (selectedClusters && selectedClusters.length > 0) {
      setSelectedClusterSet(new Set(selectedClusters));
      
      // Check if "Select All" should be included
      const clusters = getAvailableClusters();
      if (clusters.length > 0 && selectedClusters.length >= clusters.length) {
        setAllClustersSelected(true);
      } else {
        setAllClustersSelected(false);
      }
    } else {
      setSelectedClusterSet(new Set());
      setAllClustersSelected(false);
    }
  }, [selectedClusters, environmentData]);

  // Update selected environment when it changes from parent
  useEffect(() => {
    if (selectedEnvironment !== onSelectEnvironment) {
      setSelectedEnvironment(selectedEnvironment);
    }
  }, [selectedEnvironment]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clusterDropdownRef.current && !clusterDropdownRef.current.contains(event.target)) {
        setIsClusterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get available clusters from environmentData
  const getAvailableClusters = () => {
    return Array.isArray(environmentData)
      ? [...new Set(environmentData.map(item => item.cluster))]
      : [];
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleEnvironmentChange = (event) => {
    const environment = event.target.value;
    setSelectedEnvironment(environment);
    onSelectEnvironment(environment);
    
    // Reset clusters when environment changes
    setSelectedClusterSet(new Set());
    setAllClustersSelected(false);
    onSelectCluster([]);
    
    // Reset search
    setSearchTerm('');
    onSearch('');
  };

  const handleClusterCheckboxChange = (cluster) => {
    const newSelectedClusters = new Set(selectedClusterSet);
    const clusters = getAvailableClusters();
    
    if (cluster === 'Select All') {
      if (newSelectedClusters.has('Select All')) {
        // Unselect all
        newSelectedClusters.clear();
        setAllClustersSelected(false);
      } else {
        // Select all
        newSelectedClusters.clear();
        newSelectedClusters.add('Select All');
        clusters.forEach(c => newSelectedClusters.add(c));
        setAllClustersSelected(true);
      }
    } else {
      if (newSelectedClusters.has(cluster)) {
        // Unselect this cluster
        newSelectedClusters.delete(cluster);
        newSelectedClusters.delete('Select All');
        setAllClustersSelected(false);
      } else {
        // Select this cluster
        newSelectedClusters.add(cluster);
        
        // Check if all clusters are now selected
        const allSelected = clusters.every(c => newSelectedClusters.has(c));
        if (allSelected) {
          newSelectedClusters.add('Select All');
          setAllClustersSelected(true);
        }
      }
    }
    
    setSelectedClusterSet(newSelectedClusters);
    // Remove 'Select All' for the callback to parent
    const clustersToReport = Array.from(newSelectedClusters).filter(c => c !== 'Select All');
    onSelectCluster(clustersToReport);
  };

  const handleSelectAllClusters = (checked) => {
    const clusters = getAvailableClusters();
    const newSelectedClusters = new Set();
    
    if (checked) {
      newSelectedClusters.add('Select All');
      clusters.forEach(cluster => newSelectedClusters.add(cluster));
    }
    
    setAllClustersSelected(checked);
    setSelectedClusterSet(newSelectedClusters);
    
    // Remove 'Select All' for the callback to parent
    const clustersToReport = checked ? clusters : [];
    onSelectCluster(clustersToReport);
  };

  const getClusterDisplayText = () => {
    if (selectedClusterSet.size === 0) return 'Select Cluster';
    if (selectedClusterSet.has('Select All')) return 'All Clusters';
    
    const clusterArray = Array.from(selectedClusterSet).filter(c => c !== 'Select All');
    if (clusterArray.length === 1) return clusterArray[0];
    return `${clusterArray.length} selected`;
  };

  const TooltipContent = ({ items }) => (
    <div className="tooltip-content">
      {Array.from(items).filter(item => item !== 'Select All').join(', ')}
    </div>
  );

  const toggleClusterDropdown = () => {
    if (selectedEnvironment) {
      setIsClusterOpen(!isClusterOpen);
    }
  };

  const clusters = getAvailableClusters();

  const filteredClusters = clusters.filter(cluster =>
    cluster.toLowerCase().includes(clusterSearchTerm.toLowerCase())
  );

  // Prevent dropdown from closing when clicking search input
  const handleSearchClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="dropdown-container">
      <div className="aws-account-container">
        <label htmlFor="env">AWS Account:</label>
        <select 
          id="env" 
          onChange={handleEnvironmentChange}
          value={selectedEnvironment}
        >
          <option value="">Select Account</option>
          {environments.map((env) => (
            <option key={env} value={env}>
              {env}
            </option>
          ))}
        </select>
      </div>

      <div className="dropdown-item" ref={clusterDropdownRef}>
        <label htmlFor="cluster">Cluster:</label>
        <div className="namespace-dropdown">
          <div 
            className={`namespace-select-header ${!selectedEnvironment ? 'disabled' : ''}`}
            onClick={toggleClusterDropdown}
            onMouseEnter={() => setShowClusterTooltip(true)}
            onMouseLeave={() => setShowClusterTooltip(false)}
          >
            {getClusterDisplayText()}
            {showClusterTooltip && selectedClusterSet.size > 0 && (
              <TooltipContent items={selectedClusterSet} />
            )}
          </div>
          {isClusterOpen && (
            <div className="namespace-options">
              <div className="search-container" onClick={handleSearchClick}>
                <input
                  type="text"
                  placeholder="Search clusters..."
                  value={clusterSearchTerm}
                  onChange={(e) => setClusterSearchTerm(e.target.value)}
                  className="dropdown-search"
                />
              </div>
              <div className="namespace-option select-all">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={allClustersSelected}
                    onChange={(e) => handleSelectAllClusters(e.target.checked)}
                  />
                  <span className="checkbox-text">Select All</span>
                </label>
              </div>
              {filteredClusters.map((cluster, index) => (
                <div key={index} className="namespace-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedClusterSet.has(cluster)}
                      onChange={() => handleClusterCheckboxChange(cluster)}
                    />
                    <span className="checkbox-text">{cluster}</span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="search-bar">
        <label htmlFor="search"></label>
        <input
          type="text"
          id="search"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>
    </div>
  );
};

export default NodeDropbox;